/**
 * Cart checkout consent gate — cart page and cart drawer.
 * Blocks checkout until terms are accepted; then navigates to checkout reliably.
 */
(function () {
  const WALLET_SELECTOR =
    '.additional-checkout-buttons, shopify-accelerated-checkout-cart, shop-pay-wallet-button, shopify-google-pay-button';
  const CONSENT_SELECTOR = '.cart__checkout-consent, .cart__checkout-agree-checkbox';

  /** @param {Element} ctas */
  function requiresConsent(ctas) {
    return ctas.getAttribute('data-requires-checkout-consent') === 'true';
  }

  /** @param {Element} ctas */
  function isConsentGranted(ctas) {
    const agree = ctas.querySelector('.cart__checkout-agree-checkbox');
    return Boolean(agree && agree.checked);
  }

  /** @param {Element} ctas */
  function getCheckoutUrl(ctas) {
    return ctas.getAttribute('data-checkout-url') || '/checkout';
  }

  /** @param {Element} ctas */
  function setBlockedState(ctas, blocked) {
    if (blocked) {
      ctas.setAttribute('data-checkout-consent-blocked', 'true');
    } else {
      ctas.removeAttribute('data-checkout-consent-blocked');
    }

    const checkout = ctas.querySelector('button[name="checkout"][form="cart-form"]');
    if (checkout) {
      if (blocked) {
        checkout.disabled = true;
        checkout.setAttribute('disabled', 'disabled');
        checkout.setAttribute('aria-disabled', 'true');
      } else {
        checkout.disabled = false;
        checkout.removeAttribute('disabled');
        checkout.removeAttribute('aria-disabled');
      }
    }

    const wallets = ctas.querySelector('.cart__checkout-wallets');
    if (wallets) {
      wallets.classList.toggle('cart__checkout-wallets--blocked', blocked);
      if (blocked) {
        wallets.setAttribute('inert', '');
        wallets.setAttribute('aria-hidden', 'true');
      } else {
        wallets.removeAttribute('inert');
        wallets.removeAttribute('aria-hidden');
      }
    }
  }

  /** @param {Element} ctas */
  function syncCtas(ctas) {
    if (!requiresConsent(ctas)) {
      setBlockedState(ctas, false);
      return;
    }
    setBlockedState(ctas, !isConsentGranted(ctas));
  }

  function syncAll() {
    document.querySelectorAll('.cart__ctas[data-requires-checkout-consent="true"]').forEach(syncCtas);
  }

  function unifyConsentFromPeers(changed) {
    if (!(changed instanceof HTMLInputElement) || !changed.classList.contains('cart__checkout-agree-checkbox')) {
      return;
    }
    document.querySelectorAll('.cart__checkout-agree-checkbox').forEach((box) => {
      if (box !== changed) box.checked = changed.checked;
    });
    syncAll();
  }

  /** @param {Element} ctas */
  function proceedToCheckout(ctas) {
    const checkoutUrl = getCheckoutUrl(ctas);
    window.location.assign(checkoutUrl);
  }

  /** @param {Event} event */
  function isWalletCheckoutTarget(event) {
    if (!(event.target instanceof Element)) return false;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    return path.some(
      (node) =>
        node instanceof Element &&
        (node.matches(WALLET_SELECTOR) || Boolean(node.closest(WALLET_SELECTOR)))
    );
  }

  /** @param {Event} event */
  function blockWalletWhenConsentPending(event) {
    if (!(event.target instanceof Element)) return;

    const blockedCtas = event.target.closest('.cart__ctas[data-checkout-consent-blocked="true"]');
    if (!blockedCtas) return;
    if (event.target.closest(CONSENT_SELECTOR)) return;
    if (!isWalletCheckoutTarget(event)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    blockedCtas.querySelector('.cart__checkout-agree-checkbox')?.focus();
  }

  /** @param {Event} event */
  function onDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    const checkoutBtn = event.target.closest('button[name="checkout"][form="cart-form"]');
    if (!(checkoutBtn instanceof HTMLButtonElement)) {
      blockWalletWhenConsentPending(event);
      return;
    }

    const ctas = checkoutBtn.closest('.cart__ctas');
    if (!ctas || !requiresConsent(ctas)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!isConsentGranted(ctas)) {
      ctas.querySelector('.cart__checkout-agree-checkbox')?.focus();
      return;
    }

    checkoutBtn.disabled = false;
    checkoutBtn.removeAttribute('disabled');
    checkoutBtn.removeAttribute('aria-disabled');
    proceedToCheckout(ctas);
  }

  let syncScheduled = false;
  function scheduleSync() {
    if (syncScheduled) return;
    syncScheduled = true;
    requestAnimationFrame(() => {
      syncScheduled = false;
      syncAll();
    });
  }

  document.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.classList.contains('cart__checkout-agree-checkbox')) {
      unifyConsentFromPeers(event.target);
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.classList.contains('cart__checkout-agree-checkbox')) {
      unifyConsentFromPeers(event.target);
    }
  });

  document.addEventListener('click', onDocumentClick, true);
  document.addEventListener('pointerdown', blockWalletWhenConsentPending, true);

  document.addEventListener('cart:update', scheduleSync);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        scheduleSync();
        break;
      }
    }
  });

  function observeCartRoots() {
    document.querySelectorAll('cart-items-component, .cart-drawer__summary, .cart-page__summary').forEach((root) => {
      observer.observe(root, { childList: true, subtree: true });
    });
  }

  function init() {
    syncAll();
    observeCartRoots();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
